/**
 * AdminProviderDetailsHero Component
 * Admin-specific wrapper for ProviderDetailsHero with inline editing
 * 
 * Wraps key fields with InlineEditWrapper for quick editing:
 * - companyName (text field)
 * - tagline (text field)
 * - phoneNumber (text field)
 * - websiteUrl (text field)
 * - hoursDescription (text field)
 * 
 * Delegates to original ProviderDetailsHero for non-editable content
 */

import { Star, MapPin, Clock, DollarSign, Heart, MessageCircle, Share2, Calendar, Users, Award } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { InlineEditWrapper } from '../inline-edit/InlineEditWrapper';
import { InlineTextField } from '../inline-edit/InlineTextField';
import type { ProviderProfile } from '@/features/provider-directory/types/provider-profile.types';
import { hasValue, hasArrayData } from '@/features/provider-directory/utils/fieldValidation';
import { useContactAction } from '@/features/provider-directory/hooks/useContactAction';

type AdminProviderDetailsHeroProps = {
  provider: ProviderProfile;
  displayRating: number | null;
  displayReviewCount: number;
  reviewsLoading: boolean;
  serviceRangesText?: string;
  canFavorite: boolean;
  canAddToNetwork: boolean;
  currentFavoriteState: boolean;
  currentNetworkState: boolean;
  onFavoriteToggle: () => void;
  onNetworkToggle: () => void;
  onMessageProvider: () => void;
  onScheduleCall: () => void;
  onShareProvider?: () => void;
  isFavoriteLoading: boolean;
  isNetworkLoading: boolean;
  /** Callback to update field values */
  onFieldUpdate: (fieldName: string, value: string) => Promise<void>;
};

/**
 * Admin-specific Hero component with inline editing
 * Wraps ProviderDetailsHero with InlineEditWrapper for key fields
 */
export function AdminProviderDetailsHero({
  provider,
  displayRating,
  displayReviewCount,
  reviewsLoading,
  serviceRangesText,
  canFavorite,
  canAddToNetwork,
  currentFavoriteState,
  currentNetworkState,
  onFavoriteToggle,
  onNetworkToggle,
  onMessageProvider,
  onScheduleCall,
  onShareProvider,
  isFavoriteLoading,
  isNetworkLoading,
  onFieldUpdate,
}: AdminProviderDetailsHeroProps) {
  const primaryCategory = provider.primaryCategory || provider.categories[0]?.name || '';
  const deliveryTypes = provider.serviceDeliveryTypes || serviceRangesText || '';
  const ratingValue = displayRating ?? 0;
  // Feature 103: admin preview always shows the schedule button whenever
  // either field is configured — reuses the same precedence as the public
  // profile/card instead of re-deriving the calendlyUrl-or-intakeFormUrl check.
  const { primary, hasCalendlySecondary } = useContactAction(provider, true);
  const hasScheduleAction = primary === 'intake-form' || hasCalendlySecondary;

  // Handle share action
  const handleShare = () => {
    if (onShareProvider) {
      onShareProvider();
    } else {
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
      });
    }
  };

  return (
    <Card className="overflow-hidden mb-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Hero Media Section - NOT editable inline (use Information tab) */}
        <div className="relative h-96">
          {provider.heroMediaUrl && provider.mediaType === 'video' ? (
            <video 
              src={provider.heroMediaUrl}
              className="w-full h-full object-cover"
              controls
              autoPlay
              muted
              loop
            />
          ) : provider.heroMediaUrl && provider.mediaType === 'image' ? (
            <img
              src={provider.heroMediaUrl}
              alt={provider.companyName}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : provider.user?.profilePhoto ? (
            <img
              src={provider.user.profilePhoto}
              alt={`${provider.companyName} profile`}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#026857] to-[#378F94] flex items-center justify-center">
              <div className="text-white text-6xl font-bold">
                {provider.companyName.charAt(0)}
              </div>
            </div>
          )}
          
          {/* Action Buttons Overlay */}
          <div className="absolute top-4 right-4 flex gap-2">
            {canFavorite && (
              <Button 
                size="sm" 
                variant="secondary" 
                className="bg-white/90 hover:bg-white"
                onClick={onFavoriteToggle}
                disabled={isFavoriteLoading}
              >
                <Heart className={`w-4 h-4 ${currentFavoriteState ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>
            )}
            <Button 
              size="sm" 
              variant="secondary" 
              className="bg-white/90 hover:bg-white"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Info Section */}
        <div className="p-8">
          <div className="mb-4">
            {/* Category and Service Delivery Types - Graceful degradation */}
            {(hasValue(primaryCategory) || hasValue(deliveryTypes)) && (
              <div className="mb-4">
                {hasValue(primaryCategory) && (
                  <Badge className="bg-[#026857] hover:bg-[#026857] text-white text-base px-4 py-2 mb-3">
                    {primaryCategory}
                  </Badge>
                )}
                {hasValue(deliveryTypes) && (
                  <p className="text-sm text-gray-600 mb-2">{deliveryTypes}</p>
                )}
              </div>
            )}
            
            {/* Company Name - EDITABLE */}
            <InlineEditWrapper
              value={provider.companyName}
              onSave={onFieldUpdate}
              fieldName="provider.companyName"
              displayComponent={({ value }) => (
                <h1 className="text-3xl text-gray-900 mb-2">{value}</h1>
              )}
              editComponent={InlineTextField}
            />
            
            {/* Tagline - EDITABLE - Graceful degradation */}
            {hasValue(provider.tagline) && (
              <InlineEditWrapper
                value={provider.tagline || ''}
                onSave={onFieldUpdate}
                fieldName="provider.tagline"
                displayComponent={({ value }) => (
                  <p className="text-lg text-gray-600 mb-4">{value}</p>
                )}
                editComponent={InlineTextField}
              />
            )}
            
            {/* Rating stars - NOT editable */}
            {/* Graceful degradation: only show if provider has placeId (Google integration) */}
            {hasValue(provider.placeId) && (
              <div className="flex items-center gap-2 mb-4">
                {reviewsLoading ? (
                  <div className="flex items-center gap-2 text-gray-400">
                    <span className="text-sm">Loading reviews...</span>
                  </div>
                ) : ratingValue > 0 ? (
                  <div className="flex items-center gap-1">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < Math.floor(ratingValue)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-gray-900 ml-1">{ratingValue.toFixed(1)}</span>
                    <span className="text-gray-500">({displayReviewCount} reviews)</span>
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">No reviews yet</span>
                )}
              </div>
            )}

            {/* Key Information - Graceful degradation: hide section if no data */}
            {(hasArrayData(provider.servedCounties) || 
              hasValue(provider.hoursDescription) || 
              hasValue(provider.priceRangeIndicator) ||
              hasValue(provider.yearsInBusiness) ||
              hasValue(provider.establishedYear)) && (
              <div className="space-y-2 mb-6">
                {/* Served counties - NOT editable inline */}
                {hasArrayData(provider.servedCounties) && (
                  <div className="flex items-start gap-2 text-gray-700">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1 text-sm">
                      {provider.servedCounties.map((county, idx) => (
                        <span key={idx}>
                          {county}
                          {idx < provider.servedCounties.length - 1 && <span className="mr-1">,</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Hours description - EDITABLE */}
                {hasValue(provider.hoursDescription) && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <InlineEditWrapper
                      value={provider.hoursDescription || ''}
                      onSave={onFieldUpdate}
                      fieldName="provider.hoursDescription"
                      displayComponent={({ value }) => <span className="text-sm">{value}</span>}
                      editComponent={InlineTextField}
                    />
                  </div>
                )}
                
                {/* Price range - NOT editable inline (use select in Information tab) */}
                {hasValue(provider.priceRangeIndicator) && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{provider.priceRangeIndicator}</span>
                  </div>
                )}

                {/* Years in business and established year */}
                {(hasValue(provider.yearsInBusiness) || hasValue(provider.establishedYear)) && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Award className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">
                      {hasValue(provider.yearsInBusiness) && `${provider.yearsInBusiness} years in business`}
                      {hasValue(provider.yearsInBusiness) && hasValue(provider.establishedYear) && ' • '}
                      {hasValue(provider.establishedYear) && `Established ${provider.establishedYear}`}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            {canAddToNetwork && (
              <Button
                onClick={onNetworkToggle}
                disabled={isNetworkLoading}
                variant={currentNetworkState ? 'outline' : 'default'}
                className={currentNetworkState ? '' : 'bg-[#026857] hover:bg-[#025247] text-white'}
              >
                <Users className="w-4 h-4 mr-2" />
                {currentNetworkState ? 'Connected' : 'Add to Network'}
              </Button>
            )}
            
            <Button
              onClick={onMessageProvider}
              variant="outline"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Message
            </Button>
            
            {/* Feature 103: intakeFormUrl treated like calendlyUrl in the admin
                preview — button shows, handler stays noop (no real iframe here). */}
            {hasScheduleAction && (
              <Button
                onClick={onScheduleCall}
                className="bg-[#026857] hover:bg-[#025247] text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Call
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

